"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var client_instance_id_1 = require("@services/instance-context/client-instance-id");
var override_service_1 = require("@services/overrides/override-service");
var unique_string_1 = require("@services/utils/unique-string");
var feature_flipper_1 = require("@shared/constants/feature-flipper");
var environment_1 = require("@shared/types/environment");
var angular_1 = require("angular");
var lodash_1 = __importDefault(require("lodash"));
/**
 * Unintended errors thrown in the interceptor logic should be suppressed
 * to avoid unexpectedly breaking network request callbacks.
 * So keep track of any errors that are thrown intentionally.
 */
var expectedErrorTracker = (function () {
    var prefix = 'EXPECTED_ERROR_PREFIX';
    return {
        createExpectedError: function (message) {
            return new Error(prefix + ": " + message);
        },
        isExpectedError: function (error) {
            var errorMessage = lodash_1.default.get(error, 'message');
            return lodash_1.default.startsWith(errorMessage, prefix);
        }
    };
})();
var WRITE_METHODS = new Set(['PATCH', 'PUT', 'POST', 'DELETE']);
var isWriteRequest = function (method) {
    return WRITE_METHODS.has(method);
};
(function (module) {
    'use strict';
    module.factory('pr.httpInterceptor', [
        '$injector',
        '$q',
        'prOverrideTrigger',
        'rsEnvironment',
        function (
        // @ts-ignore
        $injector, 
        // @ts-ignore
        $q, triggerOverrides, 
        // @ts-ignore
        env) {
            var clientInstanceIdUpdatePromise;
            function getSavedFiltersService() {
                return $injector.get('prSavedFiltersService');
            }
            /**
             * Checks if the request will eventually make a request to RE/AE
             * Excludes /data route as it was already intercepted in prDataRequestService
             * @param  {IRequestConfig} config
             * @return {boolean}
             */
            function isDataRequest(config) {
                var API_ROUTES = $injector.get('pr.API_ROUTES');
                var ROUTES_TO_INTERCEPT = [
                    API_ROUTES.DISPLAY_LOGIC,
                    API_ROUTES.EXPORT,
                    API_ROUTES.SAVED_FILTERS_PROXY,
                    API_ROUTES.THREE_SIXTY_RATER_DISPLAY_LOGIC
                ];
                return config && config.method === 'POST' && config.data &&
                    lodash_1.default.some(ROUTES_TO_INTERCEPT, function (route) {
                        return lodash_1.default.includes(config.url, route);
                    });
            }
            /**
             * This adds necessary information to any POST requests
             * that make RE/AE requests which do not go through the /data route.
             * @param  {IRequestConfig} config
             * @return {IRequestConfig} modified config
             */
            function modifyDataRequest(config) {
                var dataRequestService = $injector.get('pr.dataRequestService');
                config.data = dataRequestService.generateOptions(config.data);
                return config;
            }
            /**
             * Checks if the request will modify the report state.
             * @param  {IRequestConfig} config
             * @return {boolean}
             */
            function isReportStateUpdateRequest(config) {
                return isWriteRequest(config.method) && lodash_1.default.includes(config.url, 'reports/ripsaw/containers');
            }
            /**
             * Adds any necessary information that is needed for state update requests.
             * Also throws an error if edits are not enabled for the report.
             * @param {ExtendedRequestConfig} config
             * @returns {ExtendedRequestConfig} modified config
             */
            function modifyStateUpdateRequest(config) {
                var reportStatus = $injector.get('pr.reportStatus');
                var editAbilityManager = $injector.get('pr.editAbilityManager');
                if (!editAbilityManager.isEnabled()) {
                    throw expectedErrorTracker.createExpectedError('Editing is not enabled');
                }
                config.id = getIdForStateUpdateRequest(config);
                reportStatus.savePending(config.id);
                reportStatus.modifySaveRequest(config);
                return config;
            }
            /**
             * Gets the id to associate with the request
             * to keep track of pending/failed/succeeded requests.
             * @param {IRequestConfig} config
             * @returns {string} the id for the request
             */
            function getIdForStateUpdateRequest(config) {
                /**
                 * widget state update failures can be recovered with future updates to the same widget
                 * because later updates will overwrite any previous updates anyway.
                 */
                var areErrorsRecoverable = config.method === 'PUT' &&
                    lodash_1.default.includes(config.url, '/widgets/Widget_') &&
                    lodash_1.default.endsWith(config.url, '/state');
                if (areErrorsRecoverable) {
                    /**
                     * base the id on the method and url so that widget update requests always have the same id.
                     * this is needed so that failed updates can recover with future successful updates
                     */
                    return config.method + ":" + config.url;
                }
                return unique_string_1.getUniqueString();
            }
            function modifyStateUpdateResponse(response) {
                var _a;
                var reportStatus = $injector.get('pr.reportStatus');
                reportStatus.saveSucceeded(response.config.id);
                if ((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.lastUpdatedDate) {
                    reportStatus.updateLastUpdatedDate(response.data.lastUpdatedDate);
                    response.data = response.data.original;
                }
            }
            function isSavedFiltersUpdate(config) {
                return isWriteRequest(config.method) &&
                    getSavedFiltersService().isSavedFiltersUrl(config.url);
            }
            /**
             * On the first save, we need to update the client instance id
             * on the context meta of the container in order to detect
             * 409 conflicts. We should update the client instance id by
             * hitting our server directly so we do not put the patch on the queue.
             *
             * @returns {IPromise} promise from updating the client instance id
             */
            function updateClientInstanceId() {
                if (!clientInstanceIdUpdatePromise) {
                    // Inject in line to avoid circular dependency issues
                    var $http = $injector.get('$http');
                    var API_ROUTES = $injector.get('pr.API_ROUTES');
                    var activeReport = $injector.get('prActiveReport');
                    var containerId = activeReport.getReportId();
                    var newClientInstanceId = {
                        lastUpdatedClientInstanceId: client_instance_id_1.getClientInstanceId()
                    };
                    clientInstanceIdUpdatePromise = $http.post(API_ROUTES.CONTAINERS + "/" + containerId + "/updateClientInstanceId", newClientInstanceId).catch(function (error) {
                        console.error('There was an unexpected error updating the client instance id', error);
                    });
                }
                return clientInstanceIdUpdatePromise;
            }
            return {
                /**
                 * Intercepts and modifies all $http requests before they are sent to the server.
                 *
                 * Currently, this adds necessary information to any POST requests
                 * that make RE/AE requests which do not go through the /data route.
                 *
                 * Because this intercepts ALL http requests, all unexpected errors should be suppressed.
                 *
                 * @param  {IRequestConfig} config
                 * @return {IRequestConfig} modified config
                 */
                request: function requestInterceptor(config) {
                    return $q.resolve().then(function () {
                        if (isDataRequest(config)) {
                            return modifyDataRequest(config);
                        }
                        else if (isReportStateUpdateRequest(config)) {
                            return updateClientInstanceId().then(function () {
                                return modifyStateUpdateRequest(config);
                            });
                        }
                        else if (isSavedFiltersUpdate(config)) {
                            getSavedFiltersService().clearCache();
                        }
                        return $q.when(config);
                        // @ts-ignore
                    }).catch(function (error) {
                        if (expectedErrorTracker.isExpectedError(error)) {
                            throw error;
                        }
                        /**
                         * An error was thrown for some unknown reason while modifying the request.
                         * Suppress the error so we do not unexpectedly break any network requests.
                         */
                        console.error('Unexpected error intercepting request', error);
                        return config;
                    });
                },
                /**
                 * Intercepts all successful http responses
                 * Because this intercepts ALL http responses, all errors should be suppressed.
                 */
                response: function responseInterceptor(response) {
                    return $q.resolve().then(function () {
                        if (isReportStateUpdateRequest(response.config)) {
                            modifyStateUpdateResponse(response);
                        }
                        if (env.get(environment_1.ENV_VARS.FEATURES)[feature_flipper_1.FEATURES.PROCESS_PIPED_TEXT]) {
                            return triggerOverrides({
                                event: override_service_1.OVERRIDE_EVENTS.DATA_HTTP_RESPONSE,
                                data: response
                            });
                        }
                        return response;
                        // @ts-ignore
                    }).catch(function (error) {
                        console.error('Error intercepting response', error);
                        return response;
                    });
                },
                /**
                 * Intercepts all failed http responses.
                 * Because this intercepts ALL http responses, all errors created here should be suppressed.
                 */
                responseError: function responseErrorInterceptor(response) {
                    return $q.resolve().then(function () {
                        if (isReportStateUpdateRequest(response.config)) {
                            var reportStatus = $injector.get('pr.reportStatus');
                            var errorMessage = lodash_1.default.get(response, 'data.error');
                            reportStatus.saveFailed(response.config.id, errorMessage);
                        }
                        // @ts-ignore
                    }).catch(function (error) {
                        console.error('Error intercepting responseError', error);
                    }).then(function () {
                        // must throw a failed promise to mimic functionality without the interceptor
                        return $q.reject(response);
                    });
                }
            };
        }
    ]);
})(angular_1.module('qualtrics.pr'));
